import { z } from "zod";
import { dateTimeSchema, enforceSameTenant, nonEmptyStringSchema, sha256Schema, tenantOwnedShape, visibilityShape, } from "./common.js";
import { enforceVisibilityInvariant } from "./common.js";
import { extractionManifestIdSchema, sourceItemIdSchema, sourceTextPayloadIdSchema, visibilityRuleIdSchema, } from "./ids.js";
import { connectionRefSchema, sourceItemRefSchema } from "./refs.js";
import { attachmentTypeSchema, completenessStateSchema, graphProcessingStateSchema, providerSchema, sourceKindSchema, sourceLifecycleStateSchema, } from "./vocabulary.js";
const emailAddressSchema = z
    .object({
    address: z.string().email().max(320),
    displayName: z.string().max(320).nullable(),
})
    .strict();
export const emailMetadataSchema = z
    .object({
    ...tenantOwnedShape,
    sourceItemRef: sourceItemRefSchema,
    providerMessageId: nonEmptyStringSchema,
    conversationId: nonEmptyStringSchema.nullable(),
    conversationIndex: nonEmptyStringSchema.nullable(),
    internetMessageId: nonEmptyStringSchema.nullable(),
    inReplyTo: nonEmptyStringSchema.nullable(),
    references: z.array(nonEmptyStringSchema).max(1000),
    from: emailAddressSchema.nullable(),
    to: z.array(emailAddressSchema).max(2000),
    cc: z.array(emailAddressSchema).max(2000),
    bcc: z.array(emailAddressSchema).max(2000),
    replyTo: z.array(emailAddressSchema).max(100),
    sentAt: dateTimeSchema.nullable(),
    receivedAt: dateTimeSchema.nullable(),
    mailboxId: nonEmptyStringSchema,
    folderId: nonEmptyStringSchema.nullable(),
})
    .strict()
    .superRefine((value, context) => {
    enforceSameTenant(value.tenantId, [{ tenantId: value.sourceItemRef.tenantId, path: ["sourceItemRef"] }], context);
});
const eventResponseStatuses = [
    "none",
    "organizer",
    "tentativelyAccepted",
    "accepted",
    "declined",
    "notResponded",
];
const eventResponseStatusSchema = z.enum(eventResponseStatuses);
const eventAttendeeTypes = ["required", "optional", "resource"];
const eventAttendeeSchema = z
    .object({
    address: z.string().email().max(320),
    displayName: z.string().max(320).nullable(),
    type: z.enum(eventAttendeeTypes),
    responseStatus: eventResponseStatusSchema,
})
    .strict();
export const eventMetadataSchema = z
    .object({
    ...tenantOwnedShape,
    sourceItemRef: sourceItemRefSchema,
    providerEventId: nonEmptyStringSchema,
    iCalUid: nonEmptyStringSchema.nullable(),
    seriesMasterId: nonEmptyStringSchema.nullable(),
    calendarId: nonEmptyStringSchema.nullable(),
    organizer: emailAddressSchema.nullable(),
    attendees: z.array(eventAttendeeSchema).max(2000),
    startAt: dateTimeSchema.nullable(),
    endAt: dateTimeSchema.nullable(),
    isAllDay: z.boolean(),
    location: z.string().max(1024).nullable(),
    responseStatus: eventResponseStatusSchema.nullable(),
})
    .strict()
    .superRefine((value, context) => {
    enforceSameTenant(value.tenantId, [{ tenantId: value.sourceItemRef.tenantId, path: ["sourceItemRef"] }], context);
});
const chatTypes = ["oneOnOne", "group", "meeting"];
const chatTypeSchema = z.enum(chatTypes);
export const chatMetadataSchema = z
    .object({
    ...tenantOwnedShape,
    sourceItemRef: sourceItemRefSchema,
    providerChatId: nonEmptyStringSchema,
    providerMessageId: nonEmptyStringSchema,
    chatType: chatTypeSchema,
    from: emailAddressSchema.nullable(),
    /**
     * Stable provider-native Teams actor identity. Graph chat messages normally
     * do not include an email/UPN, so dropping `from.user.id` here would make
     * authorship impossible to materialize without guessing. Defaults preserve
     * compatibility with durable records written before this field existed.
     */
    providerActorId: nonEmptyStringSchema.max(512).nullable().default(null),
    /** Display-only surface; never an identifier or cross-provider merge key. */
    providerActorDisplayName: z
        .string()
        .trim()
        .min(1)
        .max(512)
        .nullable()
        .default(null),
    sentAt: dateTimeSchema.nullable(),
    lastModifiedAt: dateTimeSchema.nullable(),
    replyToMessageId: nonEmptyStringSchema.nullable(),
    participantCount: z.number().int().nonnegative().nullable(),
})
    .strict()
    .superRefine((value, context) => {
    enforceSameTenant(value.tenantId, [{ tenantId: value.sourceItemRef.tenantId, path: ["sourceItemRef"] }], context);
});
const whatsappChatTypeSchema = z.enum(["individual", "group"]);
const whatsappControlSafeIdSchema = nonEmptyStringSchema
    .max(256)
    // eslint-disable-next-line no-control-regex -- these ids form U+001F keys
    .regex(/^[^\u0000-\u001f]+$/u, "must not contain C0 control characters");
/**
 * Provider-native routing identity for one WhatsApp message. It lives on the
 * source item as well as in the side table so the deterministic source-graph
 * stage can connect chats, replies and authors without reading provider state.
 * Message text and media bytes are deliberately absent.
 */
export const whatsappMetadataSchema = z
    .object({
    ...tenantOwnedShape,
    sourceItemRef: sourceItemRefSchema,
    providerChatJid: whatsappControlSafeIdSchema,
    providerMessageId: whatsappControlSafeIdSchema,
    fromMe: z.boolean(),
    chatType: whatsappChatTypeSchema,
    senderJid: nonEmptyStringSchema.max(256).nullable(),
    pushName: z.string().trim().min(1).max(512).nullable(),
    sentAt: dateTimeSchema.nullable(),
    editedAt: dateTimeSchema.nullable(),
    deletedAt: dateTimeSchema.nullable(),
    replyToMessageId: whatsappControlSafeIdSchema.nullable(),
    /** Null for legacy/ambiguous quotes; graph materialization then omits reply_to. */
    replyToFromMe: z.boolean().nullable().default(null),
    participantCount: z.number().int().nonnegative().nullable(),
})
    .strict()
    .superRefine((value, context) => {
    enforceSameTenant(value.tenantId, [{ tenantId: value.sourceItemRef.tenantId, path: ["sourceItemRef"] }], context);
    if (value.replyToMessageId === null && value.replyToFromMe !== null) {
        context.addIssue({
            code: "custom",
            path: ["replyToFromMe"],
            message: "replyToFromMe requires replyToMessageId.",
        });
    }
});
export const driveContainerKindSchema = z.enum(["onedrive", "sharepoint"]);
/**
 * Provider-native identity for an item stored in OneDrive or a selected
 * SharePoint document library. The binary is deliberately absent: connector
 * record blobs may carry bounded extracted text, but never the downloaded file
 * bytes or Graph's short-lived download URL.
 */
export const driveItemMetadataSchema = z
    .object({
    ...tenantOwnedShape,
    sourceItemRef: sourceItemRefSchema,
    containerKind: driveContainerKindSchema,
    driveId: nonEmptyStringSchema,
    siteId: nonEmptyStringSchema.nullable(),
    providerItemId: nonEmptyStringSchema,
    parentItemId: nonEmptyStringSchema.nullable(),
    name: z.string().trim().min(1).max(1024),
    webUrl: z.string().url().nullable(),
    mimeType: nonEmptyStringSchema.max(255).nullable(),
    sizeBytes: z.number().int().nonnegative(),
    etag: nonEmptyStringSchema.nullable(),
    ctag: nonEmptyStringSchema.nullable(),
    createdAt: dateTimeSchema.nullable(),
    lastModifiedAt: dateTimeSchema.nullable(),
    /**
     * Graph's authoritative `createdBy.user` for the item, kept only when the
     * provider supplies a real mail address. A drive item carries no envelope,
     * so this is the ONLY authorized answer to "who authored this document"; a
     * name printed inside the file is untrusted content and can never stand in
     * for it. An identity Graph returns without a mail address stays `null`
     * rather than becoming a display-name-only actor, because the commitment
     * path resolves people by email and a name alone is not an identity.
     *
     * `lastModifiedBy` is captured for provenance beside it but is deliberately
     * NOT a fallback for `createdBy`: the two are different people whenever a
     * document was edited by someone else, and silently substituting one for
     * the other would assert a promise by a person who never made it.
     *
     * Both default to `null` so durable records written before this field
     * existed still reparse unchanged.
     */
    createdBy: emailAddressSchema.nullable().default(null),
    lastModifiedBy: emailAddressSchema.nullable().default(null),
})
    .strict()
    .superRefine((value, context) => {
    enforceSameTenant(value.tenantId, [{ tenantId: value.sourceItemRef.tenantId, path: ["sourceItemRef"] }], context);
    if (value.containerKind === "sharepoint" && value.siteId === null) {
        context.addIssue({
            code: "custom",
            path: ["siteId"],
            message: "SharePoint drive items require their exact site ID.",
        });
    }
    if (value.containerKind === "onedrive" && value.siteId !== null) {
        context.addIssue({
            code: "custom",
            path: ["siteId"],
            message: "OneDrive items cannot carry a SharePoint site ID.",
        });
    }
});
export const attachmentDescriptorSchema = z
    .object({
    ...tenantOwnedShape,
    parentSourceRef: sourceItemRefSchema,
    providerAttachmentId: nonEmptyStringSchema,
    attachmentType: attachmentTypeSchema,
    name: z.string().max(1024),
    mediaType: nonEmptyStringSchema.max(255).nullable(),
    byteSize: z.number().int().nonnegative(),
    isInline: z.boolean(),
    contentId: nonEmptyStringSchema.nullable(),
    sourceSha256: sha256Schema.nullable(),
    processingState: completenessStateSchema,
    childSourceRef: sourceItemRefSchema.nullable(),
})
    .strict()
    .superRefine((value, context) => {
    enforceSameTenant(value.tenantId, [
        { tenantId: value.parentSourceRef.tenantId, path: ["parentSourceRef"] },
        ...(value.childSourceRef
            ? [
                {
                    tenantId: value.childSourceRef.tenantId,
                    path: ["childSourceRef"],
                },
            ]
            : []),
    ], context);
});
export const sourceItemSchema = z
    .object({
    ...tenantOwnedShape,
    ...visibilityShape,
    id: sourceItemIdSchema,
    connectionRef: connectionRefSchema,
    provider: providerSchema,
    providerTenantId: nonEmptyStringSchema.nullable(),
    providerAccountId: nonEmptyStringSchema,
    providerItemId: nonEmptyStringSchema,
    providerContainerId: nonEmptyStringSchema.nullable(),
    sourceKind: sourceKindSchema,
    sourceUrl: z.string().url().nullable(),
    sourceVersion: nonEmptyStringSchema.nullable(),
    etag: nonEmptyStringSchema.nullable(),
    contentSha256: sha256Schema.nullable(),
    visibilityRuleId: visibilityRuleIdSchema.nullable(),
    createdAt: dateTimeSchema.nullable(),
    modifiedAt: dateTimeSchema.nullable(),
    observedAt: dateTimeSchema,
    deletedAt: dateTimeSchema.nullable(),
    lifecycleState: sourceLifecycleStateSchema,
    graphProcessingState: graphProcessingStateSchema,
    parserVersion: nonEmptyStringSchema.nullable(),
    completenessState: completenessStateSchema,
    expectedNativeSegmentCount: z.number().int().nonnegative().nullable(),
    mappedNativeSegmentCount: z.number().int().nonnegative().nullable(),
    parentSourceRef: sourceItemRefSchema.nullable(),
    emailMetadata: emailMetadataSchema.nullable(),
    // Backward compatibility: record blobs persisted before the calendar slice
    // have no eventMetadata key. A missing key defaults to null on reparse, so
    // previously healthy mail records never fail the strict schema. The output
    // type stays `EventMetadata | null` and the superRefine invariants below
    // still run against the defaulted value.
    eventMetadata: eventMetadataSchema.nullable().default(null),
    // Backward compatibility: record blobs persisted before the teams slice have
    // no chatMetadata key. A missing key defaults to null on reparse, so records
    // written before Teams support never fail the strict schema. The output type
    // stays `ChatMetadata | null` and the superRefine invariants below still run
    // against the defaulted value.
    chatMetadata: chatMetadataSchema.nullable().default(null),
    // Backward compatibility: WhatsApp records written before graph routing
    // metadata lived only in the connector envelope. Missing remains null.
    whatsappMetadata: whatsappMetadataSchema.nullable().default(null),
    // Backward compatibility: blobs and typed fixtures written before Microsoft
    // Files support do not contain this additive key. Missing and null both mean
    // no drive metadata.
    driveItemMetadata: driveItemMetadataSchema.nullable().optional(),
    attachments: z.array(attachmentDescriptorSchema).max(10000),
})
    .strict()
    .superRefine((value, context) => {
    enforceVisibilityInvariant(value, context);
    if (value.ownerMemberId === null) {
        context.addIssue({
            code: "custom",
            path: ["ownerMemberId"],
            message: "SourceItem always requires the connection owner member ID.",
        });
    }
    const references = [
        {
            tenantId: value.connectionRef.tenantId,
            path: ["connectionRef"],
        },
        ...(value.parentSourceRef
            ? [
                {
                    tenantId: value.parentSourceRef.tenantId,
                    path: ["parentSourceRef"],
                },
            ]
            : []),
        ...(value.emailMetadata
            ? [
                {
                    tenantId: value.emailMetadata.tenantId,
                    path: ["emailMetadata"],
                },
            ]
            : []),
        ...(value.eventMetadata
            ? [
                {
                    tenantId: value.eventMetadata.tenantId,
                    path: ["eventMetadata"],
                },
            ]
            : []),
        ...(value.chatMetadata
            ? [
                {
                    tenantId: value.chatMetadata.tenantId,
                    path: ["chatMetadata"],
                },
            ]
            : []),
        ...(value.whatsappMetadata
            ? [
                {
                    tenantId: value.whatsappMetadata.tenantId,
                    path: ["whatsappMetadata"],
                },
            ]
            : []),
        ...(value.driveItemMetadata
            ? [
                {
                    tenantId: value.driveItemMetadata.tenantId,
                    path: ["driveItemMetadata"],
                },
            ]
            : []),
        ...value.attachments.map((attachment, index) => ({
            tenantId: attachment.tenantId,
            path: ["attachments", index],
        })),
    ];
    enforceSameTenant(value.tenantId, references, context);
    if (value.emailMetadata &&
        value.emailMetadata.sourceItemRef.sourceItemId !== value.id) {
        context.addIssue({
            code: "custom",
            path: ["emailMetadata", "sourceItemRef", "sourceItemId"],
            message: "Email metadata must reference its containing source item.",
        });
    }
    if (value.eventMetadata &&
        value.eventMetadata.sourceItemRef.sourceItemId !== value.id) {
        context.addIssue({
            code: "custom",
            path: ["eventMetadata", "sourceItemRef", "sourceItemId"],
            message: "Event metadata must reference its containing source item.",
        });
    }
    if (value.chatMetadata &&
        value.chatMetadata.sourceItemRef.sourceItemId !== value.id) {
        context.addIssue({
            code: "custom",
            path: ["chatMetadata", "sourceItemRef", "sourceItemId"],
            message: "Chat metadata must reference its containing source item.",
        });
    }
    if (value.whatsappMetadata &&
        value.whatsappMetadata.sourceItemRef.sourceItemId !== value.id) {
        context.addIssue({
            code: "custom",
            path: ["whatsappMetadata", "sourceItemRef", "sourceItemId"],
            message: "WhatsApp metadata must reference its containing source item.",
        });
    }
    if (value.driveItemMetadata &&
        value.driveItemMetadata.sourceItemRef.sourceItemId !== value.id) {
        context.addIssue({
            code: "custom",
            path: ["driveItemMetadata", "sourceItemRef", "sourceItemId"],
            message: "Drive metadata must reference its containing source item.",
        });
    }
    // Each source kind carries exactly its own structured metadata; the email,
    // calendar and teams side records mutually exclude one another. Binding each
    // metadata to its kind implies the exclusion: a kind can only hold the one
    // metadata bound to it, so the other two must stay null.
    if (value.emailMetadata !== null && value.sourceKind !== "email_message") {
        context.addIssue({
            code: "custom",
            path: ["emailMetadata"],
            message: "Email metadata is only valid for an email message source item.",
        });
    }
    if (value.eventMetadata !== null && value.sourceKind !== "calendar_event") {
        context.addIssue({
            code: "custom",
            path: ["eventMetadata"],
            message: "Event metadata is only valid for a calendar event source item.",
        });
    }
    if (value.chatMetadata !== null && value.sourceKind !== "teams_message") {
        context.addIssue({
            code: "custom",
            path: ["chatMetadata"],
            message: "Chat metadata is only valid for a teams message source item.",
        });
    }
    if (value.whatsappMetadata !== null &&
        value.sourceKind !== "whatsapp_message") {
        context.addIssue({
            code: "custom",
            path: ["whatsappMetadata"],
            message: "WhatsApp metadata is only valid for a WhatsApp message source item.",
        });
    }
    if (value.driveItemMetadata != null && value.sourceKind !== "drive_item") {
        context.addIssue({
            code: "custom",
            path: ["driveItemMetadata"],
            message: "Drive metadata is only valid for a drive item source item.",
        });
    }
    value.attachments.forEach((attachment, index) => {
        if (attachment.parentSourceRef.sourceItemId !== value.id) {
            context.addIssue({
                code: "custom",
                path: ["attachments", index, "parentSourceRef", "sourceItemId"],
                message: "Attachment descriptor must reference its containing source item.",
            });
        }
    });
    if (value.expectedNativeSegmentCount !== null &&
        value.mappedNativeSegmentCount !== null &&
        value.mappedNativeSegmentCount > value.expectedNativeSegmentCount) {
        context.addIssue({
            code: "custom",
            path: ["mappedNativeSegmentCount"],
            message: "Mapped segments cannot exceed expected segments.",
        });
    }
    if (value.completenessState === "success" &&
        value.expectedNativeSegmentCount !== value.mappedNativeSegmentCount) {
        context.addIssue({
            code: "custom",
            path: ["completenessState"],
            message: "Successful completeness requires every native segment to be mapped.",
        });
    }
});
export const sourceTextPayloadMetadataSchema = z
    .object({
    ...tenantOwnedShape,
    id: sourceTextPayloadIdSchema,
    sourceItemRef: sourceItemRefSchema,
    payloadKind: z.enum([
        "email_headers",
        "email_plain",
        "email_html",
        "mime_part",
        "pdf_page",
        "docx_part",
        "calendar_html",
        "calendar_text",
        "teams_message_text",
        "whatsapp_message_text",
        "drive_item_text",
    ]),
    segmentCount: z.number().int().nonnegative(),
    byteLength: z.number().int().nonnegative(),
    contentSha256: sha256Schema,
    immutable: z.literal(true),
    storedAt: dateTimeSchema,
})
    .strict()
    .superRefine((value, context) => {
    enforceSameTenant(value.tenantId, [{ tenantId: value.sourceItemRef.tenantId, path: ["sourceItemRef"] }], context);
});
export const sourceTextPayloadSchema = sourceTextPayloadMetadataSchema;
export const extractionManifestSchema = z
    .object({
    ...tenantOwnedShape,
    id: extractionManifestIdSchema,
    sourceItemRef: sourceItemRefSchema,
    parserVersion: nonEmptyStringSchema.nullable(),
    sourceSha256: sha256Schema.nullable(),
    canonicalSha256: sha256Schema.nullable(),
    completenessState: completenessStateSchema,
    expectedNativeSegmentCount: z.number().int().nonnegative(),
    mappedNativeSegmentCount: z.number().int().nonnegative(),
    expectedAttachmentCount: z.number().int().nonnegative(),
    accountedAttachmentCount: z.number().int().nonnegative(),
    checkedAt: dateTimeSchema,
})
    .strict()
    .superRefine((value, context) => {
    enforceSameTenant(value.tenantId, [{ tenantId: value.sourceItemRef.tenantId, path: ["sourceItemRef"] }], context);
    if (value.mappedNativeSegmentCount > value.expectedNativeSegmentCount) {
        context.addIssue({
            code: "custom",
            path: ["mappedNativeSegmentCount"],
            message: "Mapped segments cannot exceed expected segments.",
        });
    }
    if (value.accountedAttachmentCount > value.expectedAttachmentCount) {
        context.addIssue({
            code: "custom",
            path: ["accountedAttachmentCount"],
            message: "Accounted attachments cannot exceed expected attachments.",
        });
    }
    if (value.completenessState === "success" &&
        (value.parserVersion === null ||
            value.sourceSha256 === null ||
            value.canonicalSha256 === null ||
            value.mappedNativeSegmentCount !== value.expectedNativeSegmentCount ||
            value.accountedAttachmentCount !== value.expectedAttachmentCount)) {
        context.addIssue({
            code: "custom",
            path: ["completenessState"],
            message: "Successful extraction requires hashes, parser version, and reconciled segment and attachment counts.",
        });
    }
});
//# sourceMappingURL=source.js.map